import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  TemplateRef,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, filter, take, tap } from 'rxjs';

import { DprForecastData, WarningStatusType } from '@dpr-details/enums';
import { DprDetailsGridBaseComponent } from '@dpr-details/models/grid';
import { DprForecastActivityGridData } from '@dpr-details/models/grid/dpr-forecast-activity-grid-data.model';
import { DprActivitiesGridService } from '@dpr-details/services/grid';
import {
  DateDisplayComponent,
  DateEditComponent,
  SelectDisplayComponent,
  SelectEditComponent,
} from '@grid/components';
import { GridAlignment, GridSortMode, GridSortDirection } from '@grid/enums';
import {
  GridApi,
  GridColumn,
  GridOptions,
  GridSync,
  SortDescriptor,
} from '@grid/models';
import {
  selectSortValueAccessor,
  selectTooltipMessage,
} from '@grid/utils/select';
import {
  ConfirmDialogComponent,
  TimeDisplayComponent,
  TimeEditComponent,
} from '@shared/components';
import { SelectionItem } from '@shared/models';
import {
  Activity,
  ActivityGroup,
  ForecastCategory,
  Location,
  SubprojectConfiguration,
} from '@shared/models/project-setup';
import { GridColumns, GridDefaultLabels } from '@shared/utils/grid';
import {
  compareTime,
  Constants,
  convertTimeToDate,
  getStaticMessage,
  toUTCDate,
} from '@shared/utils';
import { StaticMessage, UtcDateTimeOptions } from '@shared/enums';
import { MatDialog } from '@angular/material/dialog';
import {
  DprForecastActivityCreateService,
  DprForecastActivityStatusService,
} from '@dpr-details/services';
import { DprEditForecastActivityDialogEditComponent } from '@dpr-details/containers';
import { UtcDateAdapter } from '@shared/material/date-picker';

@UntilDestroy()
@Component({
  selector: 'app-dpr-forecast-activities-grid',
  templateUrl: './dpr-forecast-activities-grid.component.html',
  styleUrls: ['./dpr-forecast-activities-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprForecastActivitiesGridComponent
  extends DprDetailsGridBaseComponent<DprForecastActivityGridData>
  implements OnChanges
{
  //#region Templates

  @ViewChild('statusTemplate', { static: true })
  statusTemplate: TemplateRef<unknown>;

  @ViewChild('actionsTemplate', { static: true })
  actionsTemplate: TemplateRef<unknown>;

  @ViewChild('durationTemplate', { static: true })
  durationTemplate: TemplateRef<unknown>;

  @ViewChild('infoTemplate', { static: true })
  infoTemplate: TemplateRef<unknown>;

  @ViewChild('sortTemplate', { static: true })
  sortTemplate: TemplateRef<unknown>;

  //#endregion

  DprForecastData = DprForecastData;

  gridEntityName = getStaticMessage(StaticMessage.ActivityColumn);
  forecastCategoryFieldName = $localize`:@@dprDetails.dprForecastActivitiesGrid.forecastCategory:Category`;
  activityGroupFieldName = getStaticMessage(StaticMessage.ActivityGroupColumn);
  locationFieldName = getStaticMessage(StaticMessage.LocationColumn);
  dateFieldName = $localize`:@@dprDetails.dprForecastActivitiesGrid.txtDate:Date`;
  startFieldName = getStaticMessage(StaticMessage.StartColumn);
  finishFieldName = getStaticMessage(StaticMessage.FinishColumn);

  ascendingDefinition: SortDescriptor<DprForecastActivityGridData>[] = [
    {
      columnDef: 'forecastCategoryId',
      direction: GridSortDirection.Ascending,
      sortValueAccessor: selectSortValueAccessor(
        this.getSelectForecastCategoriesOptions.bind(this)
      ),
    },
    {
      columnDef: 'date',
      direction: GridSortDirection.Ascending,
      sortValueAccessor: (_, data) =>
        data?.date ? new Date(data.date).getTime() : null,
    },
    { columnDef: 'start', direction: GridSortDirection.Ascending },
    { columnDef: 'finish', direction: GridSortDirection.Ascending },
  ];

  constructor(
    protected activatedRoute: ActivatedRoute,
    protected router: Router,
    private dprActivitiesGridService: DprActivitiesGridService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private utcDateAdapter: UtcDateAdapter,
    private dprForecastActivityCreateService: DprForecastActivityCreateService,
    private dprForecastActivityStatusService: DprForecastActivityStatusService
  ) {
    super();
  }

  //#region Getters

  get activities(): Activity[] {
    return this.projectSetup.activityList;
  }

  get forecastCategories(): ForecastCategory[] {
    return this.projectSetup.forecastCategories;
  }

  get activityGroups(): ActivityGroup[] {
    return this.projectSetup.activityGroupList;
  }

  get locations(): Location[] {
    return this.projectSetup.locationList;
  }

  get subprojectConfiguration(): SubprojectConfiguration {
    return this.projectSetup.subprojectConfiguration;
  }

  //#endregion

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.canEdit) {
      this.initializeGrid();
    }
  }

  initializeGrid(): void {
    this.gridColumns$.next(this.getGridColumns());
    this.gridOptions$.next(this.getGridOptions());
  }

  checkRouteParameters(): void {
    setTimeout(() => {
      combineLatest([
        this.gridApi$.asObservable(),
        this.activatedRoute.queryParams,
      ])
        .pipe(
          untilDestroyed(this),
          filter(([gridApi]) => !!gridApi),
          take(1),
          tap(([gridApi, params]) => {
            if (isNaN(params.selectedTab)) {
              this.navigateToBaseRoute();
              return;
            }
            this.openModalFromRoute(gridApi, {
              // this is needed so that he user doesn't receive an empty modal in case they change the selectedTab parameter
              // to a tab that doesn't exists
              selectedTab: Object.values(DprForecastData).includes(
                +params.selectedTab
              )
                ? +params.selectedTab
                : DprForecastData.CustomData,
            });
          })
        )
        .subscribe();
    });
  }

  isRowEmpty(data: DprForecastActivityGridData): boolean {
    return (
      data.activityGroupId == null &&
      data.activityId == null &&
      data.locationId == null &&
      String.isNullOrWhiteSpace(data.start) &&
      String.isNullOrWhiteSpace(data.finish) &&
      !data.dprForecastSubActivities?.length &&
      !data.dprForecastActivityCustomDataList?.length
    );
  }

  validateChanges(changes: GridSync<DprForecastActivityGridData>): boolean {
    const entities = [
      ...(changes?.addedList ?? []),
      ...(changes?.updatedList ?? []),
    ];
    const errorMessages = [];

    for (const entity of entities) {
      if (!entity.forecastCategoryId) {
        errorMessages.push(
          getStaticMessage(
            StaticMessage.FieldMandatoryError,
            this.forecastCategoryFieldName
          )
        );
      }

      if (!entity.activityGroupId) {
        errorMessages.push(
          getStaticMessage(
            StaticMessage.FieldMandatoryError,
            this.activityGroupFieldName
          )
        );
      }

      if (!entity.activityId) {
        errorMessages.push(
          getStaticMessage(
            StaticMessage.FieldMandatoryError,
            this.gridEntityName
          )
        );
      }

      if (!entity.locationId) {
        errorMessages.push(
          getStaticMessage(
            StaticMessage.FieldMandatoryError,
            this.locationFieldName
          )
        );
      }

      if (!entity.date) {
        errorMessages.push(
          getStaticMessage(
            StaticMessage.FieldMandatoryError,
            this.dateFieldName
          )
        );
      }

      if (!entity.start) {
        errorMessages.push(
          getStaticMessage(
            StaticMessage.FieldMandatoryError,
            this.startFieldName
          )
        );
      }

      if (!entity.finish) {
        errorMessages.push(
          getStaticMessage(
            StaticMessage.FieldMandatoryError,
            this.finishFieldName
          )
        );
      }

      if (
        entity.start &&
        entity.finish &&
        compareTime(
          convertTimeToDate(entity.start),
          convertTimeToDate(entity.finish)
        ) > 0
      ) {
        errorMessages.push(
          $localize`:@@dprDetails.dprForecastActivitiesGrid.errStartMustBeBeforeFinish:- Start must be before Finish.`
        );
      }

      if (errorMessages.length) {
        this.dialog.open(ConfirmDialogComponent, {
          ...Constants.DialogOptions,
          data: {
            title: getStaticMessage(StaticMessage.ValidationErrorTitle),
            message: errorMessages.join('<br>'),
            buttonLabel: getStaticMessage(StaticMessage.OkButton),
            hideCancelButton: true,
          },
        });

        return false;
      }
    }

    return true;
  }

  protected getGridOptions(): GridOptions<DprForecastActivityGridData> {
    return {
      labeling: GridDefaultLabels,
      addConfig: {
        allowAdding: this.canEdit,
        buttonPosition: 'floating',
        value: this.addRow.bind(this),
        onAdded: this.computeStatusForAllRows.bind(this),
        keepAutogeneratedIds: true,
        autoStartEdit: false,
      },
      editConfig: {
        allowEditing: this.canEdit,
      },
      deleteConfig: {
        allowDeleting: this.canEdit,
        onDeleted: this.computeStatusForAllRows.bind(this),
      },
      saveConfig: {
        isExternalSave: true,
      },
      rowConfig: {
        allowDeleting: this.canBeDeleted.bind(this),
        allowSelection: this.canBeDeleted.bind(this),
        isEmpty: this.isRowEmpty.bind(this),
      },
      sortConfig: {
        mode: GridSortMode.Fixed,
        definition: this.ascendingDefinition,
      },
      selectionConfig: {
        allowSelection: this.canEdit,
      },
      events: {
        onGridRegistered: (gridApi: GridApi<DprForecastActivityGridData>) => {
          this.gridRegistered.emit(gridApi);
          this.gridApi$.next(gridApi);
        },
        onDataReady: () => {
          this.computeStatusForAllRows();
          this.checkRouteParameters();
          this.cdr.markForCheck();
        },
      },
    };
  }

  protected getGridColumns(): GridColumn<DprForecastActivityGridData>[] {
    return [
      {
        columnDef: 'status',
        header: {
          name: $localize`:@@dprDetails.dprForecastActivitiesGrid.colStatus:Status`,
          alignment: GridAlignment.Center,
        },
        allowSorting: false,
        display: {
          type: this.statusTemplate,
        },
        width: 65,
        disableFocusedBackground: true,
        tabIndex: -1,
      },
      {
        columnDef: 'forecastCategoryId',
        header: {
          name: this.forecastCategoryFieldName,
        },
        allowSorting: false,
        display: {
          type: SelectDisplayComponent,
          config: {
            options: this.getSelectForecastCategoriesOptions.bind(this),
          },
        },
        edit: {
          type: SelectEditComponent,
          config: {
            options: this.getForecastCategoriesOptions.bind(this),
          },
        },
        tooltip: {
          message: selectTooltipMessage(
            this.getForecastCategoriesOptions.bind(this)
          ),
        },
        minWidth: 200,
      },
      {
        columnDef: 'activityGroupId',
        header: {
          name: this.activityGroupFieldName,
        },
        allowSorting: false,
        display: {
          type: SelectDisplayComponent,
          config: {
            options: this.getSelectActivityGroupOptions.bind(this),
          },
        },
        edit: {
          type: SelectEditComponent,
          config: {
            options: this.getActivityGroupOptions.bind(this),
          },
        },
        onCellEdited: this.activityGroupChanged.bind(this),
        tooltip: {
          message: selectTooltipMessage(
            this.getActivityGroupOptions.bind(this)
          ),
        },
        minWidth: 200,
      },
      {
        columnDef: 'activityId',
        header: {
          name: this.gridEntityName,
        },
        allowSorting: false,
        display: {
          type: SelectDisplayComponent,
          config: {
            options: this.getSelectActivityOptions.bind(this),
          },
        },
        edit: {
          type: SelectEditComponent,
          config: {
            options: this.getActivityOptions.bind(this),
          },
          allowEditing: (data) => !!data.activityGroupId,
        },
        onCellEdited: this.activityChanged.bind(this),
        tooltip: {
          message: selectTooltipMessage(this.getActivityOptions.bind(this)),
        },
        minWidth: 200,
        recomputeRowOnChange: true,
      },
      {
        columnDef: 'locationId',
        header: {
          name: this.locationFieldName,
        },
        allowSorting: false,
        display: {
          type: SelectDisplayComponent,
          config: {
            options: this.getSelectLocationOptions.bind(this),
          },
        },
        edit: {
          type: SelectEditComponent,
          config: {
            options: this.getLocationOptions.bind(this),
          },
          allowEditing: (data) => !!data.activityId,
        },
        onCellEdited: this.locationChanged.bind(this),
        tooltip: {
          message: selectTooltipMessage(this.getLocationOptions.bind(this)),
        },
        minWidth: 200,
      },
      {
        columnDef: 'date',
        header: {
          name: this.dateFieldName,
          alignment: GridAlignment.Right,
        },
        columnAlignment: GridAlignment.Right,
        allowSorting: false,
        display: { type: DateDisplayComponent },
        edit: {
          type: DateEditComponent,
          config: {
            options: {
              minDate: () =>
                this.utcDateAdapter.addCalendarDays(
                  toUTCDate(
                    this.dprDetails.dprDateTime,
                    UtcDateTimeOptions.UtcDateWithoutTime
                  ),
                  1
                ),
            },
            timeOptions: {
              withoutTimeZone: true,
            },
          },
          delegateEndEdit: true,
        },
        minWidth: 100,
      },
      {
        columnDef: 'start',
        header: {
          name: this.startFieldName,
          alignment: GridAlignment.Center,
        },
        columnAlignment: GridAlignment.Center,
        allowSorting: false,
        display: { type: TimeDisplayComponent },
        edit: { type: TimeEditComponent },
        onCellEdited: (data) => {
          if (this.findActivityById(data.activityId)?.isMilestone) {
            this.gridComponent.api.editCell(data, 'finish', data.start);
          }

          this.computeStatusForAllRows();
        },
        tooltip: {
          trigger: this.subprojectConfiguration
            .permanentlyDisplayStartEndDefinitions
            ? 'manual'
            : 'mouseenter',
          hideOnClick:
            this.subprojectConfiguration.permanentlyDisplayStartEndDefinitions,
          onlyTextOverflow: false,
          property: 'activityId',
          message: (context) => {
            if (context.data.activityId === null) {
              return null;
            }

            const activity = this.findActivityById(context.data.activityId);

            return activity?.startDefinition;
          },
          disabled: (context) =>
            context.data.activityId == null ||
            String.isNullOrWhiteSpace(
              this.findActivityById(context.data.activityId)?.startDefinition
            ),
        },
        width: 90,
      },
      {
        columnDef: 'finish',
        header: {
          name: this.finishFieldName,
          alignment: GridAlignment.Center,
        },
        columnAlignment: GridAlignment.Center,
        allowSorting: false,
        display: { type: TimeDisplayComponent },
        edit: {
          type: TimeEditComponent,
          allowEditing: (data) =>
            !this.findActivityById(data.activityId)?.isMilestone,
        },
        onCellEdited: () => {
          this.computeStatusForAllRows();
        },
        tooltip: {
          trigger: this.subprojectConfiguration
            .permanentlyDisplayStartEndDefinitions
            ? 'manual'
            : 'mouseenter',
          hideOnClick:
            this.subprojectConfiguration.permanentlyDisplayStartEndDefinitions,
          onlyTextOverflow: false,
          property: 'activityId',
          message: (context) => {
            if (context.data.activityId === null) {
              return null;
            }

            const activity = this.findActivityById(context.data.activityId);

            return activity?.endDefinition;
          },
          disabled: (context) =>
            context.data.activityId == null ||
            String.isNullOrWhiteSpace(
              this.findActivityById(context.data.activityId)?.endDefinition
            ),
        },
        width: 90,
      },
      {
        columnDef: 'duration',
        header: {
          name: getStaticMessage(StaticMessage.DurationColumn),
          alignment: GridAlignment.Center,
        },
        columnAlignment: GridAlignment.Center,
        allowSorting: false,
        display: { type: this.durationTemplate },
        width: 100,
      },
      GridColumns.commentFn<DprForecastActivityGridData>(
        this.getActivityTitle.bind(this)
      ),
      {
        columnDef: 'info',
        header: {
          name: $localize`:@@dprDetails.dprForecastActivitiesGrid.colInfo:Info`,
        },
        allowSorting: false,
        allowEditing: this.canEdit,
        display: { type: this.infoTemplate },
        edit: (data: DprForecastActivityGridData) => ({
          type: DprEditForecastActivityDialogEditComponent,
          mode: 'dialog',
          delegateStartEdit: true,
          config: {
            ...Constants.DialogOptions,
            additionalData: {
              selectedSubproject: this.selectedSubproject,
              isMilestoneActivity: this.activities.find(
                (x) => x.id === data.activityId
              )?.isMilestone,
              title: this.getActivityTitle(data),
              dprLockedDate: this.dprDetails.lockedDate,
            },
            beforeOpenAdaptor: (activityData, additionalData) => {
              this.navigateToBaseRouteWithParams({
                modal: 'info',
                entity: activityData.id,
                selectedTab: additionalData.selectedTab,
              });
            },
            afterCloseAdaptor: () => {
              this.navigateToBaseRoute();
            },
            panelClass: 'dialogCustom--largeSize',
          },
        }),
        onCellEdited: () => {
          this.computeStatusForAllRows();
        },
        width: 150,
        tabIndex: -1,
      },
      {
        ...GridColumns.actions,
        display: { type: this.actionsTemplate },
        allowEditing: this.canEdit,
        edit: (data: DprForecastActivityGridData) => ({
          type: DprEditForecastActivityDialogEditComponent,
          mode: 'dialog',
          delegateStartEdit: true,
          config: {
            ...Constants.DialogOptions,
            additionalData: {
              selectedSubproject: this.selectedSubproject,
              activityMobilisationList:
                this.projectSetup.activityMobilisationList,
              isMilestoneActivity: this.activities.find(
                (x) => x.id === data.activityId
              )?.isMilestone,
              title: this.getActivityTitle(data),
              dprLockedDate: this.dprDetails.lockedDate,
            },
            beforeOpenAdaptor: (activityData, additionalData) => {
              this.navigateToBaseRouteWithParams({
                modal: 'info',
                entity: activityData.id,
                selectedTab: additionalData.selectedTab,
              });
            },
            afterCloseAdaptor: () => {
              this.navigateToBaseRoute();
            },
            panelClass: 'dialogCustom--largeSize',
          },
        }),
        onCellEdited: () => {
          this.computeStatusForAllRows();
        },
      },
    ];
  }

  //#region Private methods

  private getSelectForecastCategoriesOptions(
    data: DprForecastActivityGridData
  ): SelectionItem[] {
    const forecastOptions = this.getForecastCategoriesOptions();
    if (
      data.forecastCategoryId &&
      !forecastOptions.find((x) => x.id === data.forecastCategoryId)
    ) {
      forecastOptions.push(
        this.forecastCategories.find((x) => x.id === data.forecastCategoryId)
      );
    }
    return forecastOptions;
  }

  private getForecastCategoriesOptions(): SelectionItem[] {
    return this.forecastCategories.filter((x) => x.isActive) as SelectionItem[];
  }

  private getSelectActivityGroupOptions(
    data: DprForecastActivityGridData
  ): SelectionItem[] {
    const activityGroupOptions = this.getActivityGroupOptions();

    if (
      data.activityGroupId &&
      !activityGroupOptions.find((x) => x.id === data.activityGroupId)
    ) {
      activityGroupOptions.push(
        this.activityGroups.find((x) => x.id === data.activityGroupId)
      );
    }

    return activityGroupOptions;
  }

  private getActivityGroupOptions(): SelectionItem[] {
    return this.activityGroups.filter((x) => x.isActive) as SelectionItem[];
  }

  private getSelectActivityOptions(
    data: DprForecastActivityGridData
  ): SelectionItem[] {
    const activityOptions = this.getActivityOptions(data);

    if (
      data.activityId &&
      !activityOptions.find((x) => x.id === data.activityId)
    ) {
      activityOptions.push(
        this.activities.find((x) => x.id === data.activityId)
      );
    }

    return activityOptions as SelectionItem[];
  }

  private getActivityOptions(
    data: DprForecastActivityGridData
  ): SelectionItem[] {
    if (!data.activityGroupId) {
      return [];
    }

    const activityGroups = this.dprActivitiesGridService.getActivityOptions(
      data.activityGroupId,
      this.activities,
      data.activityId
    );

    return activityGroups as SelectionItem[];
  }

  private getSelectLocationOptions(
    data: DprForecastActivityGridData
  ): SelectionItem[] {
    const locationOptions = this.getLocationOptions(data);
    if (
      data.locationId &&
      !locationOptions.find((x) => x.id === data.locationId)
    ) {
      locationOptions.push(
        this.locations.find((x) => x.id === data.locationId)
      );
    }

    return locationOptions;
  }

  private getLocationOptions(
    data: DprForecastActivityGridData
  ): SelectionItem[] {
    if (!data.activityId) {
      return [];
    }

    const locations = this.dprActivitiesGridService.getLocationOptions(
      data.activityId,
      this.locations,
      this.activities,
      data.locationId
    );

    return locations as SelectionItem[];
  }

  private activityChanged(data: DprForecastActivityGridData): void {
    let selectedLocationId = data.locationId;

    if (data.activityId) {
      const locations = this.getLocationOptions(data);

      // check if the selected location is allowed for selected activity
      if (!locations.some((x) => x.id === data.locationId)) {
        selectedLocationId = null;
      }

      // auto-select the activity if there is only one possible option
      if (locations.length === 1) {
        selectedLocationId = +locations[0].id as number;
      }
    }

    if (data.locationId !== selectedLocationId) {
      this.gridComponent.api.editCell(data, 'locationId', selectedLocationId);
    }

    if (this.findActivityById(data.activityId)?.isMilestone) {
      this.gridComponent.api.editCell(data, 'finish', data.start);
    }

    data.status = this.computeRowStatus(data);
  }

  private locationChanged(data: DprForecastActivityGridData): void {
    data.status = this.computeRowStatus(data);
  }

  private addRow(): Partial<DprForecastActivityGridData> {
    const lastRow = this.gridData?.[this.gridData.length - 1];
    const result = this.dprForecastActivityCreateService.createForecastActivity(
      this.dprDetails.id,
      lastRow,
      this.projectSetup
    );

    return result;
  }

  private activityGroupChanged(data: DprForecastActivityGridData): void {
    let activityId: number = null;

    // auto-select the activity if there is only one possible option
    if (data.activityGroupId) {
      const activities = this.getActivityOptions(data);
      if (activities.length === 1) {
        activityId = +activities[0].id;
      }
    }

    if (data.activityId !== activityId) {
      this.gridComponent.api.editCell(data, 'activityId', activityId);
    }

    data.status = this.computeRowStatus(data);
  }

  private computeRowStatus(
    data: DprForecastActivityGridData
  ): WarningStatusType {
    return this.dprForecastActivityStatusService.getStatus(
      data,
      this.projectSetup
    );
  }

  private computeStatusForAllRows(): void {
    this.gridData.forEach((data) => {
      data.status = this.computeRowStatus(data);
    });
  }

  private canBeDeleted(data: DprForecastActivityGridData): boolean {
    return data.canBeDeleted == null || data.canBeDeleted;
  }

  private getActivityTitle(data: DprForecastActivityGridData): string {
    const activityGroupName =
      this.activityGroups.find((x) => x.id === data.activityGroupId)?.name ??
      this.activityGroupFieldName;

    const activityName =
      this.findActivityById(data.activityId)?.name ?? this.gridEntityName;

    return `${activityGroupName}, ${activityName}`;
  }

  private findActivityById(activityId: number): Activity {
    return this.projectSetup.activityList.find((x) => x.id === activityId);
  }

  //#endregion
}
